import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, User, Bot, Loader2, UserCircle, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEscalated, setIsEscalated] = useState(false);
  const [registrationData, setRegistrationData] = useState({ name: '', email: '' });
  const messagesEndRef = useRef(null);

  // Check for existing session
  useEffect(() => {
    const savedSession = localStorage.getItem('chat_session_id');
    const savedName = localStorage.getItem('chat_user_name');
    if (savedSession && savedName) {
      setSessionId(savedSession);
      setIsRegistered(true);
      loadChatHistory(savedSession);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChatHistory = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/chat/history/${sid}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setIsEscalated(data.status === 'escalated');
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registrationData.name || !registrationData.email) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setIsRegistered(true);
        localStorage.setItem('chat_session_id', data.session_id);
        localStorage.setItem('chat_user_name', registrationData.name);
        localStorage.setItem('chat_user_email', registrationData.email);
        
        // Add welcome message
        setMessages([{
          role: 'assistant',
          content: data.welcome_message,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to register:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !sessionId) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: inputMessage
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.escalated) {
          setIsEscalated(true);
        }

        setMessages(prev => [...prev, {
          role: data.waiting_for_admin ? 'system' : 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Sorry, there was an error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscalate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/escalate?session_id=${sessionId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        setIsEscalated(true);
        setMessages(prev => [...prev, {
          role: 'system',
          content: "You've been connected to our support team. An agent will respond shortly.",
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to escalate:', error);
    }
  };

  const handleEmailHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/email-history?session_id=${sessionId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const email = localStorage.getItem('chat_user_email');
        alert(`Chat history will be sent to ${email}`);
      }
    } catch (error) {
      console.error('Failed to email history:', error);
    }
  };

  const handleNewChat = () => {
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_user_name');
    localStorage.removeItem('chat_user_email');
    setSessionId(null);
    setIsRegistered(false);
    setMessages([]);
    setIsEscalated(false);
    setRegistrationData({ name: '', email: '' });
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        className="fixed bottom-6 right-24 w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-500 rounded-full shadow-lg flex items-center justify-center text-white z-50 hover:shadow-xl transition-shadow"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-widget-button"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-24 w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-gray-200"
            data-testid="chat-widget-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Bot className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold">TRADERSCAPITAL</h3>
                    <p className="text-xs text-white/80">
                      {isEscalated ? '🟢 Connected to agent' : '🤖 AI Support'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-white/80 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {!isRegistered ? (
              /* Registration Form */
              <div className="flex-1 p-6 flex flex-col justify-center">
                <div className="text-center mb-6">
                  <MessageCircle className="w-12 h-12 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-heading font-bold text-lg text-gray-900">Start a Conversation</h4>
                  <p className="text-sm text-gray-500 mt-1">Please enter your details to begin</p>
                </div>
                
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-gray-700">Name</Label>
                    <div className="relative mt-1">
                      <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={registrationData.name}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, name: e.target.value }))}
                        className="pl-10"
                        required
                        data-testid="chat-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-700">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={registrationData.email}
                        onChange={(e) => setRegistrationData(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10"
                        required
                        data-testid="chat-email-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white"
                    disabled={isLoading}
                    data-testid="chat-start-btn"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Chat'}
                  </Button>
                </form>
              </div>
            ) : (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          msg.role === 'user'
                            ? 'bg-purple-600 text-white rounded-br-md'
                            : msg.role === 'admin'
                            ? 'bg-green-500 text-white rounded-bl-md'
                            : msg.role === 'system'
                            ? 'bg-yellow-100 text-yellow-800 text-center w-full text-sm'
                            : 'bg-white text-gray-800 shadow-sm rounded-bl-md border border-gray-100'
                        }`}
                      >
                        {msg.role === 'admin' && (
                          <p className="text-xs text-green-100 mb-1">Support Agent</p>
                        )}
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white rounded-2xl px-4 py-3 shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Quick Actions */}
                {!isEscalated && (
                  <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 flex gap-2">
                    <button
                      onClick={handleEscalate}
                      className="text-xs text-purple-600 hover:underline"
                    >
                      Talk to Human
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={handleEmailHistory}
                      className="text-xs text-purple-600 hover:underline"
                    >
                      Email Chat
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={handleNewChat}
                      className="text-xs text-purple-600 hover:underline"
                    >
                      New Chat
                    </button>
                  </div>
                )}

                {/* Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder={isEscalated ? "Type your message for the agent..." : "Type your message..."}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      className="flex-1"
                      disabled={isLoading}
                      data-testid="chat-message-input"
                    />
                    <Button 
                      type="submit" 
                      size="icon"
                      className="bg-purple-600 hover:bg-purple-700"
                      disabled={isLoading || !inputMessage.trim()}
                      data-testid="chat-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
